/**
 * Helpers for turning a failed streaming download into something an admin (and Sentry) can act on.
 *
 * Streaming downloads POST a form into a hidden iframe (see `useStreamDownload`). That gives us no
 * XHR/fetch object, so there is no status code to read — the only evidence of what went wrong is
 * whatever the server rendered into the iframe. dmc-server's `ServerExceptionHandler` answers a
 * failed download with a `DmcResponse` JSON body:
 *
 *   { "notifications": [{ "date", "title", "msg", "level" }], "url" }
 *
 * where `title` is the exception class name (e.g. `UnauthorizedException`) and `msg` carries the
 * downstream status line and response body (e.g. `Message: 401 Unauthorized: "..."`). That is the
 * detail worth surfacing; previously it was read only to check for a `"status":200` marker and then
 * discarded.
 */

/** Why a streaming download failed. Kept distinct so the three cases stay tellable apart. */
export const DOWNLOAD_FAILURE_REASONS = {
  /** The server responded, and rendered something into the iframe instead of streaming a file. */
  SERVER_ERROR: 'server-error',
  /** The iframe navigated off-origin (e.g. redirected to sign-in), so its body can't be read. */
  CROSS_ORIGIN: 'cross-origin',
  /** The iframe itself failed to load — the request never produced a readable response. */
  NETWORK_ERROR: 'network-error',
}

const MAX_DETAIL_LENGTH = 500

/** Collapse runs of whitespace so multi-line server output reads as one line in a notification. */
function collapseWhitespace(text) {
  return text.replace(/\s+/g, ' ').trim()
}

/**
 * Patterns for values that could double as a live credential or identifier, matched wherever they
 * appear in server-rendered text (e.g. a downstream URL an exception message echoes back). Kept
 * narrow and value-only so the rest of the diagnostic text — host, path, error class — still reads.
 */
const SENSITIVE_PATTERNS = [
  // sessionId as a query param or JSON field, e.g. `?sessionId=...` or `"sessionId":"..."`.
  [/((?:[?&]|["'])sessionid(?:["']\s*[:=]|=)\s*["']?)[^&"'\s]+/gi, '$1[REDACTED]'],
  // `Authorization: Bearer <token>` / `Bearer <token>` wherever it shows up. Only `A-Z` is needed
  // here (not also `a-z`) since the `i` flag already folds case, and a duplicated range trips
  // Sonar's "duplicate characters in a character class" check.
  [/(bearer\s+)[A-Z0-9\-_.]+/gi, '$1[REDACTED]'],
  // Email addresses. Domain labels are matched as `(label.)+` rather than a single
  // `[A-Za-z0-9.-]+` run followed by a literal `.` — that shared-character overlap between the
  // repeated class and the following literal dot is what made an earlier version of this pattern
  // super-linear. Every quantifier is also bounded to its RFC 5321 max (64-char local part,
  // 63-char label, 8 labels, up to a 24-char TLD covers real-world and punycode TLDs) so a run of
  // non-matching lookalike text (the common case here — this text isn't guaranteed to contain an
  // email at all) can't make the engine backtrack over an unbounded span: matching now costs at
  // most a small constant per scan position instead of growing with input length.
  [/[A-Za-z0-9._%+-]{1,64}@(?:[A-Za-z0-9-]{1,63}\.){1,8}[A-Za-z]{2,24}/g, '[REDACTED_EMAIL]'],
]

/**
 * Redact values that could leak a session or credential before this text leaves the browser (e.g.
 * as a Sentry extra). Reason to be conservative here rather than dropping the field entirely: the
 * host/path/error class are what make the report actionable, so only the values are replaced.
 *
 * @param {string} text
 * @returns {string} `text` with any recognized sensitive values replaced.
 */
export function scrubSensitiveData(text) {
  if (typeof text !== 'string') return text
  return SENSITIVE_PATTERNS.reduce((redacted, [pattern, replacement]) => redacted.replace(pattern, replacement), text)
}

/**
 * @param {string} text
 * @param {number} [maxLength]
 * @returns {string} `text`, shortened with an ellipsis if it exceeds `maxLength`.
 */
export function truncate(text, maxLength = MAX_DETAIL_LENGTH) {
  if (typeof text !== 'string' || text.length <= maxLength) return text
  return `${text.slice(0, maxLength)}…`
}

/**
 * Pull a readable failure detail out of a `DmcResponse` payload.
 *
 * @param {object} payload - Parsed JSON body.
 * @returns {string|undefined} The joined notification text, or undefined if there isn't any.
 */
function describeNotifications(payload) {
  const notifications = payload?.notifications
  if (!Array.isArray(notifications) || notifications.length === 0) return undefined

  const detail = notifications
    .map((notification) => [notification?.title, notification?.msg].filter(Boolean).join(': '))
    .filter(Boolean)
    .join(' ')

  return detail || undefined
}

/**
 * Describe whatever the download iframe ended up showing.
 *
 * Prefers the structured `DmcResponse` notifications when the body is JSON; otherwise falls back to
 * the raw text, which still beats reporting nothing (browsers that pretty-print JSON, an HTML error
 * page from a gateway, and plain-text responses all land here).
 *
 * @param {string} bodyText - `iframeDocument.body.innerText`.
 * @returns {string|undefined} A one-line detail, or undefined when the body was empty.
 */
export function describeDownloadFailureBody(bodyText) {
  const text = typeof bodyText === 'string' ? bodyText.trim() : ''
  if (!text) return undefined

  try {
    const detail = describeNotifications(JSON.parse(text))
    if (detail) return truncate(collapseWhitespace(detail))
  } catch {
    // Not JSON (or not a DmcResponse) — fall through and report the raw text instead.
  }

  return truncate(collapseWhitespace(text))
}
